/**
 * useSlideOutlineDraft — orchestrates the per-slide editable outline for a
 * single presentation action proposal (``document_create`` AND
 * ``document_edit`` of a Marp deck — both ship the SAME ``[title,
 * slide_list]`` payload-schema surface, see BE
 * ``unified_schema_builder._build_marp_outline_fields``).
 *
 * Generalised May 29, 2026 (was ``useDocumentCreateSlides``): the hook never
 * cared about the action_type — it only takes the proposal id + the
 * ``slide_list`` outline lifted off ``usePayloadSchema().slideOutline``. The
 * rename makes the shared intent explicit so editing a deck reuses the exact
 * same editor path as creating one (ONE editor, ONE confirm contract).
 *
 * Sibling to ``useDocumentCreateDestination`` (same shape + ``buildModifications``
 * contract): the consumer wires ``slides`` / ``title`` + their setters into
 * ``<SlideOutlineEditor>`` (directly or via ``<DocumentCreatePreview slideOutline>``)
 * and spreads ``buildModifications()`` into the execute body on Conferma.
 *
 * Source of the initial values: the BE ``slide_list`` + ``title`` fields on
 * the unified payload-schema (``usePayloadSchema().slideOutline``). The hook
 * holds the user's edits as local controlled state, seeded once the schema
 * lands (the schema fetch is async, so the seed runs in an effect keyed on
 * the proposal id + a "schema arrived" flag rather than a lazy initializer).
 *
 * ``buildModifications`` emits ``{ title, slides }`` — exactly the keys the
 * BE reads to regenerate the Marp markdown and re-render the deck. We always
 * emit both once a slide_list exists (the BE diff/audit handles equality);
 * the keys are omitted entirely when the proposal has no slide_list so
 * non-presentation document_create / non-marp document_edit is untouched.
 */
import * as React from "react";
import type { SlideObj } from "@ando/ui";
import type { PayloadSlideOutline } from "./usePayloadSchema";

export interface SlideOutlineModifications {
  readonly title: string;
  readonly slides: SlideObj[];
}

export interface UseSlideOutlineDraftOptions {
  /** Proposal id — re-seeds the editor when the focused proposal changes. */
  readonly proposalId: string;
  /**
   * The ``slide_list`` outline lifted off the payload-schema, or
   * ``undefined`` while the schema is loading / for non-presentation
   * proposals.
   */
  readonly outline: PayloadSlideOutline | undefined;
}

export interface UseSlideOutlineDraftResult {
  /** ``true`` once a ``slide_list`` outline is available for this proposal. */
  readonly hasSlideList: boolean;
  readonly slides: ReadonlyArray<SlideObj>;
  readonly setSlides: (next: SlideObj[]) => void;
  readonly title: string;
  readonly setTitle: (next: string) => void;
  /** Allowed layout names for the ``SlideOutlineEditor`` select. */
  readonly layoutOptions: ReadonlyArray<string>;
  /**
   * Build the ``{ title, slides }`` subset to merge into the execute
   * ``modifications`` map. Returns ``null`` when there is no slide_list, so
   * the caller can skip merging for non-presentation proposals.
   */
  readonly buildModifications: () => SlideOutlineModifications | null;
}

export function useSlideOutlineDraft(
  opts: UseSlideOutlineDraftOptions,
): UseSlideOutlineDraftResult {
  const { proposalId, outline } = opts;

  const [slides, setSlides] = React.useState<ReadonlyArray<SlideObj>>([]);
  const [title, setTitle] = React.useState<string>("");
  // Track which (proposalId, schema-arrival) we've already seeded so user
  // edits are never clobbered by a re-render once the data has landed. The
  // schema arrives asynchronously, so we cannot seed in a lazy initializer.
  const seededRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!outline) return;
    // Seed exactly once per proposal — re-runs only when the proposal id
    // changes (new focused card) or the outline first becomes available.
    if (seededRef.current === proposalId) return;
    seededRef.current = proposalId;
    setSlides(outline.slides.map((s) => ({ ...s })));
    setTitle(outline.title);
  }, [proposalId, outline]);

  const layoutOptions = outline?.layoutOptions ?? [];
  const hasSlideList = outline !== undefined;

  const buildModifications =
    React.useCallback((): SlideOutlineModifications | null => {
      if (!hasSlideList) return null;
      return { title, slides: slides.map((s) => ({ ...s })) };
    }, [hasSlideList, title, slides]);

  return {
    hasSlideList,
    slides,
    setSlides,
    title,
    setTitle,
    layoutOptions,
    buildModifications,
  };
}
